import { memo } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import Button from "@Components/Button";
import { AppState } from "@Store/index";
import config from "@Config/index";
import { applyFilter, expandSearchRadius } from "@Pages/Search/data/searchPageReducer";
import {
  formatExpandAvailableCountLabel,
  getExpandAvailableCount,
  getPreservedGeoFilters,
  getRadiusDistanceDisplay,
  isEmptyGeoRadiusBanner,
  isFilteredEmptyRadiusBanner,
  isLimitedMatchesRadiusBanner,
  isLocalityGeo,
  replaceHardcodedRadiusUnit,
  resolveRadiusExpandEnabled,
} from "@Pages/Search/utils/geoRadiusExpand";
import { getLocalityLabel, getUniversityLabel } from "@Pages/Search/utils/getFilterLabel";
import {
  isPaginatedUrl,
  getSearchPageFromUrl,
} from "@Pages/Search/components/SearchList/useSearchList";
import getFilterAction from "@Pages/Search/utils/getFilterAction";
import { PAGE } from "constants/configConstants";
import getFilterActiveStatus, {
  getFiltersStateActiveStatus,
} from "@Pages/Search/utils/getFilterActiveStatus";
import { FILTERS } from "@Pages/Search/components/FilterSection/data/filtersList";
import updateFilterValueByKey from "@Pages/Search/utils/updateFilterValueByKey";
import EndOfResultsIllustration from "@Icons/EndOfResultsIllustration";
import CrossIcon from "@Icons/CrossIcon";
import LoaderIcon from "@Icons/LoaderIcon";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useTranslationHook from "hooks/useTranslationHook";
import styles from "./radiusExpandBanner.module.scss";

type Props = {
  className?: string;
  showCard?: boolean;
  /** Narrow map sidebar — use the mobile empty-state layout (stacked CTAs). */
  compact?: boolean;
};

const RadiusExpandBanner = ({ className, showCard = true, compact = false }: Props) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const {
    region,
    filters,
    itemsLength,
    expandAvailableCount,
    metaCount,
    next,
    isLoading,
    isRadiusExpanded,
    radiusExpandEnabled,
    regionRadius,
  } = useSelector(
    ({ searchPageData }: AppState) => ({
      region: searchPageData.region,
      filters: searchPageData.filters,
      itemsLength: searchPageData.searchList?.items?.length || 0,
      expandAvailableCount: getExpandAvailableCount(searchPageData.searchList?.meta),
      metaCount: searchPageData.searchList?.meta?.count,
      next: searchPageData.searchList?.next,
      isLoading: searchPageData.searchList?.readyStatus === "request",
      isRadiusExpanded: searchPageData.isRadiusExpanded,
      radiusExpandEnabled: searchPageData.radiusExpandEnabled,
      // Actual radius (metres) the inventory_filter_count API's results cover, e.g. 5000.
      // Nested under data.data alongside amenities_grouped / price stats.
      regionRadius: searchPageData.dynamicFilterCountData?.data?.data?.region_radius,
    }),
    shallowEqual,
  );

  // Expand fetch in flight — keep CTA mounted with a loader instead of collapsing early.
  const isExpandPending = isRadiusExpanded && isLoading;
  const effectiveRadiusExpandEnabled = resolveRadiusExpandEnabled(radiusExpandEnabled);

  const isEmptyGeo = isEmptyGeoRadiusBanner({
    filters,
    region,
    itemsLength,
    // Keep pre-expand banner copy while the expand request is in flight.
    isLoading: isExpandPending ? false : isLoading,
    isExpanded: isExpandPending ? false : isRadiusExpanded,
    hasNonGeoFilters: getFiltersStateActiveStatus(filters),
    expandAvailableCount,
    enabled: effectiveRadiusExpandEnabled,
  });
  const isNoMatches = isFilteredEmptyRadiusBanner({
    filters,
    region,
    itemsLength,
    isLoading: isExpandPending ? false : isLoading,
    isExpanded: isExpandPending ? false : isRadiusExpanded,
    hasNonGeoFilters: getFiltersStateActiveStatus(filters),
    expandAvailableCount,
    enabled: effectiveRadiusExpandEnabled,
  });

  // Corner case: some results exist but expand_available_count is 0, so widening the
  // radius adds nothing. Show the filter-focused banner (same as no-matches) with a
  // softened "Limited matches available" headline instead of the "view nearby" CTA.
  const isLimitedMatches = isLimitedMatchesRadiusBanner({
    filters,
    region,
    itemsLength,
    isLoading: isExpandPending ? false : isLoading,
    isExpanded: isExpandPending ? false : isRadiusExpanded,
    metaCount,
    expandAvailableCount,
    next,
    isPaginated: isPaginatedUrl(),
    currentPage: getSearchPageFromUrl(),
    hasNonGeoFilters: getFiltersStateActiveStatus(filters),
    enabled: effectiveRadiusExpandEnabled,
  });

  // Limited-matches reuses the no-matches layout (filled CTA, no divider, filter pills,
  // "clear all filters" action) — only the headline differs.
  const useFilterBannerLayout = isNoMatches || isLimitedMatches;

  const hideDivider = isEmptyGeo || useFilterBannerLayout;
  const placeName = isLocalityGeo(filters, region)
    ? getLocalityLabel(filters) || region?.name || ""
    : getUniversityLabel(filters) || region?.name || "";
  const countLabel = formatExpandAvailableCountLabel(expandAvailableCount);
  const useMiles = !config.IS_CHINA_APP;
  const { distance, unitKey } = getRadiusDistanceDisplay(regionRadius, useMiles);
  const distanceUnit = t(unitKey);
  const distanceCopy = (key: string, params: Record<string, string> = {}) =>
    replaceHardcodedRadiusUnit(
      t(key, { ...params, distance, unit: distanceUnit }),
      distanceUnit,
      useMiles,
    );
  const bodyParts = t("searchpage.searchlist.radiusexpand.body", { name: placeName }).split(
    "{count}",
  );

  const appliedFilterPills = FILTERS.filter(
    (filter) =>
      filter.name !== "University" &&
      filter.name !== "Locality" &&
      getFilterActiveStatus(filter.name, filters),
  );

  // eslint-disable-next-line no-nested-ternary
  const bannerVariant = isLimitedMatches
    ? "limited_matches"
    : // eslint-disable-next-line no-nested-ternary
    isNoMatches
    ? "no_matches"
    : isEmptyGeo
    ? "empty_geo"
    : "end_of_results";

  // Shared context on every radius-expand event so impressions and clicks can be
  // correlated in analytics.
  const eventContext = {
    banner_variant: bannerVariant,
    region_name: placeName,
    region_type: region?.region_type || "",
    expand_available_count: expandAvailableCount,
    applied_filters_count: appliedFilterPills.length,
    applied_filters: appliedFilterPills.map((filter) => filter.key).join(","),
    is_card: showCard,
  };

  const handleViewNearby = () => {
    // A focused CTA that then unmounts makes Android Chrome scroll the next
    // focusable (footer / bottom nav) into view.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    gtagEventEmitter({
      action: "radius_expand_clicked",
      category: "new_search_page",
      label: placeName,
      ...eventContext,
    });
    dispatch(expandSearchRadius());
  };

  const handleClearFilters = () => {
    // Same event as the filter-rail Clear All. `source` distinguishes the banner,
    // `context` which banner state the user was in (P1D-5464 FR-44).
    const context = isNoMatches ? "no_matches" : "empty_geo";
    gtagEventEmitter({
      action: "clear_all_filters_clicked",
      category: PAGE.SEARCH,
      source: "radius_expand_banner",
      context,
    });
    dispatch(applyFilter(getPreservedGeoFilters(filters)));
  };

  // In the empty-geo state the user has 0 results within 3 km. If they also have
  // non-geo filters applied, we can't tell whether the location or the filters are
  // to blame, so surface the same pills + clear action here (alongside the expand CTA)
  // so they can peel filters off one at a time.
  const showAppliedFilters = (useFilterBannerLayout || isEmptyGeo) && appliedFilterPills.length > 0;

  const handleRemoveFilter = (key: (typeof FILTERS)[number]["key"], name: string) => {
    // Same action as resetting that filter from the rail (`price_range_filter_reset`, etc.).
    gtagEventEmitter({
      action: getFilterAction(name as Parameters<typeof getFilterAction>[0]).replace(
        /_clicked$/,
        "_reset",
      ),
      category: PAGE.SEARCH,
      label: name,
      source: "radius_expand_banner",
    });
    dispatch(
      applyFilter({
        ...updateFilterValueByKey(key, "", filters || {}),
        ...getPreservedGeoFilters(filters),
      }),
    );
  };

  const getHeadline = () => {
    if (isLimitedMatches) return t("searchpage.searchlist.radiusexpand.limitedmatches.headline");
    if (isNoMatches) return t("searchpage.searchlist.radiusexpand.nomatches.headline");
    if (isEmptyGeo) {
      return distanceCopy("searchpage.searchlist.radiusexpand.empty.headline", { name: placeName });
    }
    return distanceCopy("searchpage.searchlist.radiusexpand.headline", { name: placeName });
  };

  const getBody = () => {
    if (useFilterBannerLayout) return t("searchpage.searchlist.radiusexpand.nomatches.body");
    if (isEmptyGeo) {
      return countLabel
        ? t("searchpage.searchlist.radiusexpand.empty.body", { count: countLabel })
        : t("searchpage.searchlist.radiusexpand.empty.body.nocount");
    }
    if (bodyParts.length === 1) return bodyParts[0];
    return (
      <>
        {bodyParts[0]}
        <span className={styles.count}>{countLabel}</span>
        {bodyParts[1]}
      </>
    );
  };

  const getCtaLabel = () => {
    if (useFilterBannerLayout) return t("searchpage.searchlist.radiusexpand.nomatches.cta");
    if (isEmptyGeo && !countLabel) return t("searchpage.searchlist.radiusexpand.empty.cta");
    return t("searchpage.searchlist.radiusexpand.cta", { count: countLabel });
  };

  return (
    <section
      className={formatClassNames(
        styles.root,
        !showCard && styles.dividerOnly,
        isEmptyGeo && styles.emptyGeo,
        useFilterBannerLayout && styles.noMatches,
        compact && styles.compact,
        className,
      )}
      data-testid={showCard ? "radius-expand-banner" : "radius-expand-divider"}
    >
      {!hideDivider && (
        <div className={styles.divider}>
          <span className={styles.rule} />
          <p className={styles.eyebrow}>{t("searchpage.searchlist.radiusexpand.eyebrow")}</p>
          <span className={styles.rule} />
        </div>
      )}
      {showCard && (
        <div className={styles.card}>
          <div className={styles.content}>
            {!isLimitedMatches && <EndOfResultsIllustration className={styles.illustration} />}
            <div className={styles.group}>
              <div className={styles.copy}>
                <h3 className={styles.headline}>{getHeadline()}</h3>
                <p className={styles.body}>{getBody()}</p>
              </div>
              {/* Limited matches: the pills are the only action (no icon, no clear-all CTA). */}
              {!isLimitedMatches && (
                <div className={styles.ctaRow}>
                  <Button
                    variant="primary"
                    className={styles.cta}
                    onClick={useFilterBannerLayout ? handleClearFilters : handleViewNearby}
                    disable={isExpandPending}
                    dataTestId={
                      useFilterBannerLayout
                        ? "radius-expand-clear-filters-cta"
                        : "radius-expand-cta"
                    }
                  >
                    {isExpandPending && <LoaderIcon fill="#fff" size={20} />}
                    {getCtaLabel()}
                  </Button>
                  {isEmptyGeo && showAppliedFilters && (
                    <Button
                      variant="outline"
                      className={styles.secondaryCta}
                      onClick={handleClearFilters}
                      dataTestId="radius-expand-clear-filters-cta"
                    >
                      {t("searchpage.searchlist.radiusexpand.nomatches.cta")}
                    </Button>
                  )}
                </div>
              )}
              {showAppliedFilters && (
                <div className={styles.appliedFilters}>
                  <span className={styles.appliedFiltersLabel}>
                    {t("searchpage.searchlist.radiusexpand.nomatches.appliedfilters")}
                  </span>
                  {appliedFilterPills.map((filter) => {
                    const pillLabel = t(filter.translationKey);
                    return (
                      <span key={filter.key} className={styles.pill}>
                        {pillLabel}
                        <button
                          type="button"
                          className={styles.pillRemove}
                          aria-label={t(
                            "searchpage.searchlist.radiusexpand.nomatches.removefilter",
                            { name: pillLabel },
                          )}
                          data-testid={`radius-expand-remove-filter-${filter.key}`}
                          onClick={() => handleRemoveFilter(filter.key, filter.name)}
                        >
                          <CrossIcon size={10} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default wrapperHOC(memo(RadiusExpandBanner), {
  componentName: "RadiusExpandBanner-SearchList-SearchPage",
  showForChina: true,
});
